import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon, LinkSlashIcon } from '@heroicons/react/24/outline';
import logo from '../assets/images/logo.svg';
import { useLanguage } from '../contexts/LanguageContext';
import { shareLinkState, ShareLinkState, shareService } from '../services/shareService';
import { projectService } from '../services/projectService';
import { fetchPublicIp, formatDate, formatFileSize, isValidEmail, startFileDownload } from '../lib/format';
import { PublicProject, Release, ShareLink } from '../types';
import { ErrorNote, Field, LoadingState, Spinner } from '../components/ui';

const VISITOR_KEY = 'apptransfer.visitor';

const PublicDownload: React.FC = () => {
  const { hash } = useParams<{ hash: string }>();
  const { t, language, setLanguage } = useLanguage();
  const [link, setLink] = useState<ShareLink | null>(null);
  const [project, setProject] = useState<PublicProject | null>(null);
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [problem, setProblem] = useState<ShareLinkState | 'error' | null>(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [formError, setFormError] = useState('');
  const [identified, setIdentified] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(VISITOR_KEY) || 'null');
      if (saved?.name && saved?.email && saved?.phone) {
        setName(saved.name);
        setEmail(saved.email);
        setPhone(saved.phone);
      }
    } catch {
      /* storage unavailable */
    }
  }, []);

  useEffect(() => {
    if (!hash) return;
    let cancelled = false;
    (async () => {
      try {
        const found = await shareService.getShareLinkByHash(hash);
        if (!found) return !cancelled && setProblem('notFound');
        const state = shareLinkState(found);
        if (state !== 'ok') return !cancelled && setProblem(state);

        const [publicProject, allReleases] = await Promise.all([
          shareService.getPublicProject(found.projectId),
          projectService.getProjectReleases(found.projectId),
        ]);
        if (cancelled) return;
        if (!publicProject) return setProblem('inactive');
        setLink(found);
        setProject(publicProject);
        setReleases(found.releaseId ? allReleases.filter((r) => r.id === found.releaseId) : allReleases);
      } catch (error) {
        console.error('Error loading share link:', error);
        if (!cancelled) setProblem('error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hash]);

  const handleIdentify = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidEmail(email)) {
      setFormError(t('public.invalidEmail'));
      return;
    }
    setFormError('');
    try {
      sessionStorage.setItem(VISITOR_KEY, JSON.stringify({ name, email, phone }));
    } catch {
      /* storage unavailable */
    }
    setIdentified(true);
  };

  const handleDownload = async (release: Release) => {
    if (!link) return;
    setDownloadingId(release.id);
    try {
      const ipAddress = await fetchPublicIp();
      await shareService.recordPublicDownload(link, release.id, { name, email, phone }, ipAddress);
      setLink({ ...link, downloadCount: link.downloadCount + 1 });
      startFileDownload(release.fileUrl, release.fileName);
      toast.success(t('releases.downloadStarted'));
    } catch (error: any) {
      console.error('Error downloading file:', error);
      toast.error(error?.code === 'permission-denied' ? t('public.limitReached') : t('releases.downloadFailed'));
    } finally {
      setDownloadingId(null);
    }
  };

  const header = (
    <header className="border-b border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-950">
      <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-2.5">
          <img src={logo} alt="" className="h-8 w-8" />
          <span className="text-[17px] font-bold tracking-tight text-gray-900 dark:text-white">AppTransfer</span>
        </div>
        <button
          type="button"
          onClick={() => setLanguage(language === 'hu' ? 'en' : 'hu')}
          className="btn btn-ghost btn-sm"
        >
          {language === 'hu' ? 'English' : 'Magyar'}
        </button>
      </div>
    </header>
  );

  if (loading && !problem) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        {header}
        <LoadingState />
      </div>
    );
  }

  if (problem || !project || !link) {
    const key = problem === 'error' ? 'error' : problem || 'notFound';
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
        {header}
        <main className="mx-auto max-w-md px-4 py-20 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800">
            <LinkSlashIcon className="h-6 w-6" />
          </div>
          <h1 className="mt-5 text-xl font-bold text-gray-900 dark:text-white">{t('public.unavailableTitle')}</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t(`public.unavailable.${key}`)}</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {header}
      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('public.sharedWithYou')}</p>
        <h1 className="mt-1 text-3xl font-bold tracking-tight text-gray-900 dark:text-white">{project.name}</h1>
        {project.description && (
          <p className="mt-2 max-w-2xl whitespace-pre-wrap text-[15px] text-gray-600 dark:text-gray-300">{project.description}</p>
        )}

        {!identified ? (
          <form onSubmit={handleIdentify} className="card mt-8 space-y-4 p-6">
            <div>
              <h2 className="font-semibold text-gray-900 dark:text-white">{t('public.formTitle')}</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('public.formText')}</p>
            </div>
            <Field label={t('public.name')} htmlFor="visitor-name">
              <input id="visitor-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} autoComplete="name" />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label={t('public.email')} htmlFor="visitor-email">
                <input id="visitor-email" type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={200} autoComplete="email" />
              </Field>
              <Field label={t('public.phone')} htmlFor="visitor-phone">
                <input id="visitor-phone" type="tel" className="input" value={phone} onChange={(e) => setPhone(e.target.value)} required maxLength={50} autoComplete="tel" placeholder="+36 20 123 4567" />
              </Field>
            </div>
            {formError && <ErrorNote>{formError}</ErrorNote>}
            <button type="submit" className="btn btn-primary w-full sm:w-auto">
              {t('public.continue')}
            </button>
          </form>
        ) : (
          <section className="mt-8">
            <div className="mb-3 flex items-baseline justify-between gap-3">
              <h2 className="font-semibold text-gray-900 dark:text-white">{t('public.available')}</h2>
              <button type="button" onClick={() => setIdentified(false)} className="text-sm text-primary-600 hover:underline dark:text-primary-400">
                {t('public.changeDetails')}
              </button>
            </div>
            {releases.length === 0 ? (
              <p className="card px-5 py-10 text-center text-sm text-gray-500">{t('public.noReleases')}</p>
            ) : (
              <ul className="space-y-3">
                {releases.map((release, index) => (
                  <li key={release.id} className={`card flex flex-col gap-4 p-5 sm:flex-row sm:items-center ${index === 0 ? 'border-signal-200 dark:border-signal-900/70' : ''}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono font-semibold text-gray-900 dark:text-white">v{release.version}</span>
                        {index === 0 && (
                          <span className="badge bg-signal-50 text-signal-700 dark:bg-signal-900/40 dark:text-signal-300">
                            {t('releases.latest')}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 font-medium text-gray-800 dark:text-gray-200">{release.title}</p>
                      {release.description && (
                        <p className="mt-2 max-w-prose whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-300">{release.description}</p>
                      )}
                      <p className="mt-2 text-xs text-gray-500">
                        <span className="font-mono">{release.fileName}</span> — {formatFileSize(release.fileSize)} —{' '}
                        {formatDate(release.uploadedAt, language)}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDownload(release)}
                      disabled={downloadingId !== null}
                      className={`btn shrink-0 ${index === 0 ? 'btn-primary' : 'btn-secondary'}`}
                    >
                      {downloadingId === release.id ? <Spinner className="h-4 w-4" /> : <ArrowDownTrayIcon className="h-4 w-4" />}
                      {t('releases.download')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <p className="mt-12 text-center text-xs text-gray-400">{t('public.privacy')}</p>
      </main>
    </div>
  );
};

export default PublicDownload;
